import { LOCATION_TYPE_LABELS, locationLabelZpl, parseLabelSize } from '@wms/domain';
import { useMemo, useState } from 'react';
import { api, errorMessage } from '../../../api';
import { downloadBlob } from '../../../files';
import { fmt } from '../../../format';
import { printZpl } from '../../../printing';
import { store } from '../../../session';
import type { LocationView, WarehouseView } from '../../../types';
import { Spinner, useLoad, useToast } from '../../../ui';
import { Barcode } from '../../../ui/Barcode';
import { Icon } from '../../../ui/icons';
import { LabelSizePicker } from '../../../ui/LabelSize';
import { ZplPreview } from '../../../ui/ZplPreview';

type Paper = 'thermal' | 'a4-24' | 'a4-21';
const PAPERS: readonly { id: Paper; label: string; perPage: number }[] = [
  { id: 'thermal', label: 'Termal etiket yazıcısı (rulo)', perPage: 1 },
  { id: 'a4-24', label: "A4 etiket kağıdı · 24'lü (70 × 37 mm)", perPage: 24 },
  { id: 'a4-21', label: "A4 etiket kağıdı · 21'li (63,5 × 38,1 mm)", perPage: 21 },
];
const PAPER_KEY = 'wms.binLabels.paper';
const SIZE_KEY = 'wms.binLabels.size';
/** Browsers print large batches slowly; more than this goes in parts (or as a ZPL file). */
const MAX_PRINT = 600;

/**
 * Bin labels: the code to read, the barcode to scan. A thermal printer gets labels drawn at the
 * size of its roll and printed through its driver (or a ZPL file for Zebra printers); A4 label
 * sheets print from the browser.
 */
export function Labels({ warehouse, codes, onClearCodes }: { warehouse: WarehouseView; codes: readonly string[] | null; onClearCodes: () => void }) {
  const toast = useToast();
  const list = useLoad(() => api<LocationView[]>('GET', `/api/admin/warehouses/${warehouse.id}/locations?active=true`), [warehouse.id]);
  const [aisle, setAisle] = useState('');
  const [rackFrom, setRackFrom] = useState('');
  const [rackTo, setRackTo] = useState('');
  const [level, setLevel] = useState('');
  const [paper, setPaper] = useState<Paper>(() => {
    const saved = store.get(PAPER_KEY);
    return saved === 'a4-24' || saved === 'a4-21' ? saved : 'thermal';
  });
  const [size, setSize] = useState(() => {
    const saved = store.get(SIZE_KEY);
    return saved && parseLabelSize(saved) ? saved : '100x150';
  });
  const [busy, setBusy] = useState(false);
  const aisles = useMemo(() => [...new Set((list.data ?? []).map((l) => l.aisle))], [list.data]);
  const chosen = useMemo(() => {
    const all = list.data ?? [];
    if (codes) return all.filter((l) => codes.includes(l.code));
    const from = Number(rackFrom) || 1;
    const to = Number(rackTo) || 99;
    return all.filter((l) => (!aisle || l.aisle === aisle) && l.rack >= from && l.rack <= to && (!level || l.level === Number(level)));
  }, [list.data, codes, aisle, rackFrom, rackTo, level]);
  const spec = PAPERS.find((p) => p.id === paper)!;
  const shown = chosen.slice(0, MAX_PRINT);
  const zplOf = (bins: readonly LocationView[]) =>
    bins.map((l) => locationLabelZpl({ code: l.code, barcode: l.barcode, size, caption: `${warehouse.code} · ${LOCATION_TYPE_LABELS[l.type]}` })).join('\n');
  const first = shown[0];
  const sample = useMemo(() => (paper === 'thermal' && first ? zplOf([first]) : null), [paper, first, size]);
  const pages = useMemo(() => {
    const out: LocationView[][] = [];
    if (paper === 'thermal') return out;
    for (let i = 0; i < shown.length; i += spec.perPage) out.push(shown.slice(i, i + spec.perPage));
    return out;
  }, [shown, spec, paper]);

  const remember = () => {
    store.set(PAPER_KEY, paper);
    store.set(SIZE_KEY, size);
  };

  const printSheets = () => {
    remember();
    const style = document.createElement('style');
    style.textContent = `@page { size: A4 portrait; margin: 0; }`;
    document.head.appendChild(style);
    document.body.classList.add('printing-labels');
    const cleanup = () => {
      style.remove();
      document.body.classList.remove('printing-labels');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 2000);
  };

  const printThermal = async () => {
    remember();
    setBusy(true);
    try {
      const { pages: printed } = await printZpl(zplOf(shown), `${warehouse.code} göz etiketleri`);
      toast(`${printed} etiket yazdırmaya gönderildi (${size.replace('x', ' × ')} mm)`, 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const downloadZpl = () => {
    remember();
    downloadBlob(new Blob([zplOf(chosen)], { type: 'text/plain;charset=utf-8' }), `${warehouse.code}-goz-etiketleri-${size}.zpl`);
  };

  if (!list.data) return <Spinner />;
  return (
    <>
      <section className="card no-print">
        <div className="card-b stack">
          {codes ? (
            <div className="row">
              <span className="chip info">{codes.length} göz seçildi</span>
              <button className="btn sm ghost" onClick={onClearCodes}>
                Filtreyle seç
              </button>
            </div>
          ) : (
            <div className="form-grid">
              <div className="field">
                <label htmlFor="lb-aisle">Sıra</label>
                <select id="lb-aisle" className="select" value={aisle} onChange={(e) => setAisle(e.target.value)}>
                  <option value="">Tümü</option>
                  {aisles.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Raf aralığı</label>
                <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                  <input className="input" inputMode="numeric" placeholder="1" value={rackFrom} onChange={(e) => setRackFrom(e.target.value)} aria-label="İlk raf" />
                  <span>–</span>
                  <input className="input" inputMode="numeric" placeholder="99" value={rackTo} onChange={(e) => setRackTo(e.target.value)} aria-label="Son raf" />
                </div>
              </div>
              <div className="field">
                <label htmlFor="lb-level">Kat</label>
                <input id="lb-level" className="input" inputMode="numeric" placeholder="Tümü" value={level} onChange={(e) => setLevel(e.target.value)} />
              </div>
            </div>
          )}
          <div className="form-grid">
            <div className="field">
              <label htmlFor="lb-paper">Nereye basılacak</label>
              <select id="lb-paper" className="select" value={paper} onChange={(e) => setPaper(e.target.value as Paper)}>
                {PAPERS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            {paper === 'thermal' && (
              <div className="field">
                <label htmlFor="lb-size">Etiket boyutu (yazıcıdaki rulo)</label>
                <LabelSizePicker id="lb-size" value={size} onChange={setSize} />
              </div>
            )}
          </div>
          <div className="row">
            {paper === 'thermal' ? (
              <>
                <button className="btn primary" disabled={busy || shown.length === 0} onClick={() => void printThermal()}>
                  <Icon name="printer" /> {fmt.n(shown.length)} etiketi yazdır
                </button>
                <button className="btn" disabled={chosen.length === 0} onClick={downloadZpl}>
                  <Icon name="download" size={18} /> ZPL dosyası indir
                </button>
              </>
            ) : (
              <button className="btn primary" disabled={shown.length === 0} onClick={printSheets}>
                <Icon name="printer" /> {fmt.n(shown.length)} etiketi yazdır
              </button>
            )}
          </div>
          {chosen.length > MAX_PRINT && <div className="banner warn">Tarayıcıdan bir seferde en fazla {MAX_PRINT} etiket basılır; kalanlar için aralığı daraltın veya ZPL kullanın.</div>}
          <span className="hint">
            {paper === 'thermal'
              ? 'Etiket bu bilgisayarın yazıcısından, seçtiğiniz boyutta basılır. Yazıcı sürücüsündeki kâğıt boyutu rulo ile aynı olmalı. Zebra yazıcılara ZPL dosyası doğrudan gönderilebilir.'
              : 'Yazdırma penceresinde kâğıt A4, kenar boşlukları "Yok", ölçek %100 olmalı.'}
          </span>
        </div>
      </section>
      {sample && (
        <section className="card no-print">
          <div className="card-h">
            <h2>Örnek etiket</h2>
            <span className="sub">
              {shown[0]?.code} · {size.replace('x', ' × ')} mm
            </span>
          </div>
          <div className="card-b">
            <ZplPreview zpl={sample} />
          </div>
        </section>
      )}
      <div className={`label-sheets ${paper}`}>
        {pages.map((page, i) => (
          <div key={i} className={`label-page ${paper}`}>
            {page.map((l) => (
              <div key={l.id} className="loc-label">
                <div className="code">{l.code}</div>
                <Barcode value={l.barcode} className="bars" />
                <div className="small">
                  {l.barcode} · {warehouse.code} · {LOCATION_TYPE_LABELS[l.type]}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
